import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  CssBaseline,
  Paper,
  Typography,
  Grid,
  Fab,
  Modal,
  CircularProgress,
  Alert,
  IconButton
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Header from "../../components/GSO/Header";
import ReportFoundItemOverlay from "./ReportFoundItemOverlay"; // Import overlay
import { useSidebar } from "../../components/SidebarContext";
import { getApiUrl, getApiHeaders } from "../../config/api";

const FoundReport = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [allItems, setAllItems] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [openOverlay, setOpenOverlay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const { isSidebarOpen } = useSidebar();
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;

  // Fetch items from backend
  useEffect(() => {
    fetchFoundItems();
  }, []);

  const fetchFoundItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(getApiUrl("found-item/list"), {
        headers: getApiHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform the data to match the expected format
      const transformedItems = data.items.map(item => ({
        id: item.id,
        name: item.item_name,
        type: item.item_type,
        description: item.additional_details || `${item.color || ''} ${item.brand_model || ''}`.trim(),
        date: item.date_found,
        time: item.time_found,
        location: item.location_found,
        uniqueId: item.unique_identifier,
        condition: item.item_condition,
        color: item.color,
        brandModel: item.brand_model,
        sizeDimension: item.size_dimension,
        photo: item.photo,
        founderId: item.founder_id,
        founderName: `${item.first_name || ''} ${item.last_name || ''}`.trim(),
        founderEmail: item.email,
        founderPhone: item.phone_number
      }));
      
      setAllItems(transformedItems);
      setFilteredData(transformedItems);
    } catch (error) {
      console.error("Error fetching found items:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort items
  useEffect(() => {
    let filtered = allItems.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.date.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.uniqueId.toLowerCase().includes(searchQuery.toLowerCase())
    );

    filtered = filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    setFilteredData(filtered);
  }, [searchQuery, sortOrder, allItems]);

  const handleOverlayClose = () => {
    setOpenOverlay(false);
    // Refresh the items list after closing the overlay
    fetchFoundItems();
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setOpenDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setOpenDetailsModal(false);
    setSelectedItem(null);
  };

  return (
    <Box
      sx={{
  transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        width: "100%",
        height: "100vh",
        bgcolor: "#FBFFE9",
        position: "relative",
        maxWidth: isSidebarOpen ? (isMobile ? "calc(100vw - 200px)" : "calc(100vw - 300px)") : "100vw",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}
    >
  <CssBaseline />
  <Header title="Found Item Reports" setSearchQuery={setSearchQuery} setSortOrder={setSortOrder} />

  <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, px: 6, flex: 1, display: "flex", flexDirection: "column", pb: 3, minHeight: 0 }}>
  <Paper elevation={4} sx={{ bgcolor: "#E1ECC8", p: 2, borderRadius: "30px", flex: 1, display: "flex", flexDirection: "column", minHeight: 0, maxHeight: { xs: 'calc(100vh - 120px)', sm: 'calc(100vh - 140px)' }, overflow: 'hidden' }}>
          <Grid container sx={{ flex: 1, minHeight: 0 }}>
            <Grid item xs={12} sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0, maxHeight: '100%' }}>
              <Paper elevation={0} sx={{ bgcolor: "#FBFFE9", borderRadius: "15px", p: 1, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                    <CircularProgress sx={{ color: "#526D50" }} />
                  </Box>
                ) : error ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                    <Alert severity="error">Error loading found items: {error}</Alert>
                  </Box>
                ) : (
                  <Box sx={{ width: "100%", bgcolor: "#FBFFE9", borderRadius: "12px", border: "1px solid #d0e7ba", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                    {/* Header Row - Fixed */}
                    <Box sx={{ display: "flex", borderBottom: "1px solid #d0e7ba", bgcolor: "#FBFFE9", minHeight: "60px", flexShrink: 0 }}>
                      {["Item Name", "Type", "Date", "Time", "Location", "Status"].map((header, index) => (
                        <Box
                          key={index}
                          sx={{
                            flex: header === "Item Name" || header === "Location" ? 1.5 : 1,
                            p: 2,
                            textAlign: "center",
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 500,
                            fontSize: "1rem",
                            color: "#526D50",
                            borderRight: index < 5 ? "1px solid #d0e7ba" : "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          {header}
                        </Box>
                      ))}
                    </Box>

                    {/* Content Area - Scrollable */}
                    <Box 
                      sx={{ 
                        flex: 1, 
                        minHeight: 0,
                        maxHeight: { xs: 'calc(100vh - 220px)', sm: 'calc(100vh - 240px)' },
                        overflowY: "auto",
                        overflowX: "hidden",
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#E1ECC8',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#526D50',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: '#3e523d',
                        }
                      }}
                    >
                      {filteredData.length === 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px", p: 3 }}>
                          <Typography variant="h6" sx={{ color: "#526D50", fontFamily: "'Poppins', sans-serif" }}>
                            No found items to display
                          </Typography>
                        </Box>
                      ) : (
                        filteredData.map((item, index) => (
                          <Box 
                            key={item.id || index} 
                            sx={{ 
                              display: "flex", 
                              borderBottom: "1px solid #d0e7ba", 
                              minHeight: "80px",
                              cursor: "pointer",
                              transition: "background-color 0.2s",
                              "&:hover": {
                                bgcolor: "#f0f7e9"
                              }
                            }}
                            onClick={() => handleItemClick(item)}
                          >
                            <Box sx={{ flex: 1.5, p: 2, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {item.name}
                            </Box>
                            <Box sx={{ flex: 1, p: 2, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {item.type}
                            </Box>
                            <Box sx={{ flex: 1, p: 2, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {item.date}
                            </Box>
                            <Box sx={{ flex: 1, p: 2, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {item.time}
                            </Box>
                            <Box sx={{ flex: 1.5, p: 2, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {item.location}
                            </Box>
                            <Box sx={{ flex: 1, p: 2, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Typography
                                sx={{
                                  bgcolor: "#526D50",
                                  color: "#FBFFE9",
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: "12px",
                                  fontSize: "0.85rem",
                                  fontWeight: 500
                                }}
                              >
                                Unclaimed
                              </Typography>
                            </Box>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={() => setOpenOverlay(true)}
        sx={{
          position: "fixed",
          bottom: 30,
          right: 30,
          backgroundColor: "#526D50",
          "&:hover": {
            backgroundColor: "#3e523d",
          },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Add Found Item Overlay Modal */}
      <Modal open={openOverlay} onClose={handleOverlayClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            boxShadow: 24,
            p: 4,
            borderRadius: "20px",
            minWidth: "400px",
            maxWidth: "90%",
          }}
        >
          <ReportFoundItemOverlay onClose={handleOverlayClose} />
        </Box>
      </Modal>

      {/* Item Details Modal */}
      <Modal open={openDetailsModal} onClose={handleCloseDetails}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#FBFFE9",
            boxShadow: 24,
            borderRadius: "20px",
            width: "95%",
            maxWidth: "1400px",
            height: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          {selectedItem && (
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
              {/* Header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
                <Typography variant="h5" sx={{ color: "#526D50", fontWeight: 600, fontFamily: "'Poppins', sans-serif" }}>
                  Found Item Details
                </Typography>
                <IconButton onClick={handleCloseDetails} sx={{ color: "#526D50" }}>
                  <Typography sx={{ fontSize: "1.5rem" }}>×</Typography>
                </IconButton>
              </Box>

              {/* Content Grid */}
              <Box sx={{ flex: 1, px: 3, pb: 2, minHeight: 0 }}>
                <Grid container spacing={2} sx={{ height: "100%" }}>
                  {/* Photo Section - Left Side */}
                  <Grid item xs={12} md={6} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    {selectedItem.photo && (
                      <Paper elevation={2} sx={{ bgcolor: "#E1ECC8", borderRadius: "15px", p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, color: "#526D50", fontWeight: 600 }}>
                          Item Photo
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#fff",
                            borderRadius: "10px",
                            overflow: "hidden",
                            minHeight: 0
                          }}
                        >
                          <Box
                            component="img"
                            src={`data:image/jpeg;base64,${selectedItem.photo}`}
                            alt={selectedItem.name}
                            sx={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain"
                            }}
                          />
                        </Box>
                      </Paper>
                    )}
                  </Grid>

                  {/* Details Section - Right Side */}
                  <Grid item xs={12} md={6} sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <Box sx={{ 
                      height: "100%", 
                      overflowY: "auto",
                      pr: 1,
                      '&::-webkit-scrollbar': { width: '6px' },
                      '&::-webkit-scrollbar-track': { background: '#E1ECC8', borderRadius: '3px' },
                      '&::-webkit-scrollbar-thumb': { background: '#526D50', borderRadius: '3px' }
                    }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {/* Item Information */}
                        <Paper elevation={2} sx={{ p: 2, bgcolor: "#E1ECC8", borderRadius: "15px" }}>
                          <Typography variant="h6" sx={{ mb: 1.5, color: "#526D50", fontWeight: 600, fontSize: "1.1rem" }}>
                            Item Information
                          </Typography>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Item Name</Typography>
                              <Typography variant="body2">{selectedItem.name}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Type</Typography>
                              <Typography variant="body2">{selectedItem.type}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Color</Typography>
                              <Typography variant="body2">{selectedItem.color || "N/A"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Brand/Model</Typography>
                              <Typography variant="body2">{selectedItem.brandModel || "N/A"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Size</Typography>
                              <Typography variant="body2">{selectedItem.sizeDimension || "N/A"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Condition</Typography>
                              <Typography variant="body2">{selectedItem.condition || "N/A"}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Unique ID</Typography>
                              <Typography variant="body2">{selectedItem.uniqueId || "N/A"}</Typography>
                            </Grid>
                          </Grid>
                        </Paper>

                        {/* Found Details */}
                        <Paper elevation={2} sx={{ p: 2, bgcolor: "#E1ECC8", borderRadius: "15px" }}>
                          <Typography variant="h6" sx={{ mb: 1.5, color: "#526D50", fontWeight: 600, fontSize: "1.1rem" }}>
                            Found Details
                          </Typography>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Location</Typography>
                              <Typography variant="body2">{selectedItem.location}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Date</Typography>
                              <Typography variant="body2">{selectedItem.date}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Time</Typography>
                              <Typography variant="body2">{selectedItem.time}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block", mb: 0.5 }}>Status</Typography>
                              <Typography
                                sx={{
                                  bgcolor: "#526D50",
                                  color: "#FBFFE9",
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: "12px",
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  display: "inline-block"
                                }}
                              >
                                Unclaimed
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>

                        {/* Additional Details */}
                        {selectedItem.description && (
                          <Paper elevation={2} sx={{ p: 2, bgcolor: "#E1ECC8", borderRadius: "15px" }}>
                            <Typography variant="h6" sx={{ mb: 1.5, color: "#526D50", fontWeight: 600, fontSize: "1.1rem" }}>
                              Additional Details
                            </Typography>
                            <Typography variant="body2">{selectedItem.description}</Typography>
                          </Paper>
                        )}

                        {/* Founder Information */}
                        {selectedItem.founderName && (
                          <Paper elevation={2} sx={{ p: 2, bgcolor: "#E1ECC8", borderRadius: "15px" }}>
                            <Typography variant="h6" sx={{ mb: 1.5, color: "#526D50", fontWeight: 600, fontSize: "1.1rem" }}>
                              Founder Information
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <Box>
                                <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Name</Typography>
                                <Typography variant="body2">{selectedItem.founderName}</Typography>
                              </Box>
                              {selectedItem.founderEmail && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Email</Typography>
                                  <Typography variant="body2">{selectedItem.founderEmail}</Typography>
                                </Box>
                              )}
                              {selectedItem.founderPhone && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: "#526D50", fontWeight: 600, display: "block" }}>Phone</Typography>
                                  <Typography variant="body2">{selectedItem.founderPhone}</Typography>
                                </Box>
                              )}
                            </Box>
                          </Paper>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default FoundReport;
